import { useEffect, useMemo, useState } from "react";

import PropTypes from "prop-types";

import Slide from "@mui/material/Slide";
import Snackbar from "@mui/material/Snackbar";
import Button from "@mui/material/Button";
import Icon from "@mui/material/Icon";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

const semanticMap = {
  error: {
    icon: "error_outline",
    accent: "#ef5350",
    background: "rgba(47, 17, 17, 0.96)",
    border: "rgba(239, 83, 80, 0.38)",
  },
  success: {
    icon: "check_circle_outline",
    accent: "#66bb6a",
    background: "rgba(18, 46, 25, 0.96)",
    border: "rgba(102, 187, 106, 0.38)",
  },
  warning: {
    icon: "warning_amber",
    accent: "#ffa726",
    background: "rgba(56, 35, 12, 0.96)",
    border: "rgba(255, 167, 38, 0.38)",
  },
  info: {
    icon: "info_outline",
    accent: "#29b6f6",
    background: "rgba(10, 39, 56, 0.96)",
    border: "rgba(41, 182, 246, 0.38)",
  },
};

function FloatSemanticMessage({
  open,
  message,
  title,
  severity,
  autoHideDuration,
  onClose,
  actionLabel,
}) {
  const [animationKey, setAnimationKey] = useState(0);

  const semantic = useMemo(() => semanticMap[severity] || semanticMap.error, [severity]);

  useEffect(() => {
    if (open) {
      setAnimationKey((current) => current + 1);
    }
  }, [open, message]);

  const handleClose = (_, reason) => {
    if (reason === "clickaway") return;
    onClose();
  };

  return (
    <Snackbar
      open={open}
      onClose={handleClose}
      autoHideDuration={autoHideDuration}
      anchorOrigin={{ vertical: "top", horizontal: "right" }}
      TransitionComponent={Slide}
      TransitionProps={{ direction: "left" }}
      sx={{ mt: 2, mr: 2 }}
    >
      <MDBox
        position="relative"
        overflow="hidden"
        minWidth={{ xs: "calc(100vw - 2rem)", sm: "25rem" }}
        maxWidth="28rem"
        px={2}
        py={1.75}
        borderRadius="xl"
        shadow="xl"
        sx={{
          background: semantic.background,
          backdropFilter: "blur(12px)",
          border: `1px solid ${semantic.border}`,
        }}
      >
        <MDBox display="flex" alignItems="flex-start" justifyContent="space-between" gap={1.5}>
          <MDBox display="flex" alignItems="flex-start" gap={1.25}>
            <MDBox
              width="2.25rem"
              height="2.25rem"
              borderRadius="lg"
              display="flex"
              alignItems="center"
              justifyContent="center"
              sx={{
                backgroundColor: "rgba(255,255,255,0.08)",
                color: semantic.accent,
                flexShrink: 0,
                lineHeight: 0,
              }}
            >
              <Icon fontSize="small" sx={{ display: "block", lineHeight: 1 }}>
                {semantic.icon}
              </Icon>
            </MDBox>
            <MDBox>
              <MDTypography variant="button" fontWeight="bold" color="white">
                {title}
              </MDTypography>
              <MDTypography
                variant="button"
                color="white"
                sx={{ display: "block", opacity: 0.84, lineHeight: 1.5, mt: 0.25 }}
              >
                {message}
              </MDTypography>
            </MDBox>
          </MDBox>
          <Button
            onClick={onClose}
            size="small"
            sx={{
              color: semantic.accent,
              minWidth: "auto",
              px: 1,
              py: 0.25,
              fontWeight: 700,
            }}
          >
            {actionLabel}
          </Button>
        </MDBox>
        <MDBox
          key={animationKey}
          position="absolute"
          left={0}
          bottom={0}
          height="3px"
          width="100%"
          sx={{
            backgroundColor: semantic.accent,
            transformOrigin: "left center",
            animation: `float-message-shrink ${autoHideDuration}ms linear forwards`,
            "@keyframes float-message-shrink": {
              from: { transform: "scaleX(1)" },
              to: { transform: "scaleX(0)" },
            },
          }}
        />
      </MDBox>
    </Snackbar>
  );
}

FloatSemanticMessage.defaultProps = {
  title: "Atenção",
  severity: "error",
  autoHideDuration: 5000,
  actionLabel: "OK",
};

FloatSemanticMessage.propTypes = {
  open: PropTypes.bool.isRequired,
  message: PropTypes.string.isRequired,
  title: PropTypes.string,
  severity: PropTypes.oneOf(["error", "success", "warning", "info"]),
  autoHideDuration: PropTypes.number,
  onClose: PropTypes.func.isRequired,
  actionLabel: PropTypes.string,
};

export default FloatSemanticMessage;
